import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { toast } from 'sonner';
import {
  ClipboardList, Save, Copy, ExternalLink, Loader2, Palette, Video, Webhook,
  MessageSquare, Mail, Check, Inbox, ArrowRight, Sun, Moon, Zap, ChevronDown, X, Code2, Link2, Globe,
} from 'lucide-react';
import { COUNTRIES } from '../lib/countries';

const WEBHOOK_FIELD_GROUPS = [
  { group: 'Submission Meta', fields: [
    ['event', 'onboarding_submission | test'],
    ['submission_id', 'obs_4bd2ae839b3ab75b'],
    ['sub_account_id', 'sub_641c356fa289'],
    ['submitted_at', '2026-07-03T16:20:00Z'],
    ['form_language', 'en | es | fr | it | de'],
  ]},
  { group: 'Business Info', fields: [
    ['business_legal_name', 'Sample Business LLC'],
    ['business_address', '123 Main Street, Austin TX'],
    ['business_street', '123 Main Street'],
    ['business_city', 'Austin'],
    ['business_state', 'TX'],
    ['business_zip', '78701'],
    ['business_country', 'United States'],
    ['business_phone', '+1 (555) 123-4567'],
    ['business_phone_country', 'US'],
    ['business_email', 'jane@samplebusiness.com'],
    ['website', 'https://samplebusiness.com'],
    ['business_type', 'Limited Liability Company (LLC)'],
    ['business_ein', '12-3456789'],
    ['area_codes', '512, 737'],
  ]},
  { group: 'Contact & Communication', fields: [
    ['first_name', 'Jane'], ['last_name', 'Doe'],
    ['team_leads', 'Jane Doe'],
    ['comm_channels', '(555) 123-4567 and jane@…'],
    ['has_team_emails', 'Yes'], ['team_emails', 'office@samplebusiness.com'],
    ['appointment_location', 'Zoom'], ['domain_access', 'I have access'],
    ['twilio_status', 'I Have Finished…'],
  ]},
  { group: 'AI Build Inputs', fields: [
    ['greeting_intro', 'Thanks for calling…'],
    ['company_details', 'Family-owned company offering…'],
    ['qualifying_questions', '1. Are you the property owner?…'],
    ['faqs_objections', 'Q: Why choose you? A:…'],
    ['has_scripts', 'Yes'], ['documents', 'sales-script.pdf'],
  ]},
  { group: 'Client Copy (checkbox on last step)', fields: [
    ['email_me_copy', 'yes | no — checkbox state'],
    ['send_client_copy', 'yes | no — same as checkbox'],
    ['client_copy_email', 'jane@samplebusiness.com (business email, only when yes)'],
    ['client_copy_summary', 'ONBOARDING SUMMARY — clean multi-line recap (only when yes)'],
  ]},
];

const PLATFORMS = ['Pumble', 'Slack', 'WhatsApp', 'Discord', 'Microsoft Teams', 'Telegram'];

export default function OnboardingBuilderPage() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const { isDarkMode } = useTheme();

  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('builder');
  const [submissions, setSubmissions] = useState([]);
  const [justSaved, setJustSaved] = useState(false);
  const [testingWebhook, setTestingWebhook] = useState(false);
  const [webhookResult, setWebhookResult] = useState(null);
  const [showWebhookFields, setShowWebhookFields] = useState(false);
  const [previewStep, setPreviewStep] = useState(0);
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const previewIframeRef = useRef(null);

  const cardBg = isDarkMode ? 'bg-[#12141a]' : 'bg-white';
  const border = isDarkMode ? 'border-white/[0.08]' : 'border-gray-200';
  const text = isDarkMode ? 'text-white' : 'text-gray-900';
  const sub = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const inputCls = `w-full px-3 py-2.5 rounded-lg border text-sm focus:outline-none focus:border-teal-400/60 ${
    isDarkMode ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'
  }`;
  const labelCls = `block text-xs font-semibold uppercase tracking-wider mb-1.5 ${sub}`;

  const fetchAll = useCallback(async () => {
    try {
      const [f, s] = await Promise.all([
        api.get(`/api/workspaces/${workspaceId}/onboarding-form`),
        api.get(`/api/workspaces/${workspaceId}/onboarding-submissions`).catch(() => ({ data: { submissions: [] } })),
      ]);
      setForm(f.data);
      setSubmissions(s.data.submissions || []);
    } catch {
      toast.error('Failed to load onboarding form');
    }
  }, [api, workspaceId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));
  const setColor = (i, val) => setForm(prev => {
    const c = [...(prev.outline_colors || ['#38bdf8', '#a855f7', '#f43f5e'])];
    c[i] = val;
    return { ...prev, outline_colors: c };
  });

  const save = async () => {
    setSaving(true);
    try {
      const { form_id, slug, workspace_id, created_at, updated_at, ...payload } = form;
      const res = await api.put(`/api/workspaces/${workspaceId}/onboarding-form`, payload);
      setForm(res.data);
      setJustSaved(true);
      toast.success('Onboarding form saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const pushPreview = useCallback((f, step) => {
    const w = previewIframeRef.current?.contentWindow;
    if (!w || !f) return;
    const { form_id, slug, workspace_id, created_at, updated_at, ...config } = f;
    w.postMessage({ type: 'ob_preview_config', config }, '*');
    w.postMessage({ type: 'ob_preview_step', step }, '*');
  }, []);

  useEffect(() => {
    const w = previewIframeRef.current?.contentWindow;
    if (!w || !form) return;
    const { form_id, slug, workspace_id, created_at, updated_at, ...config } = form;
    w.postMessage({ type: 'ob_preview_config', config }, '*');
  }, [form]);

  useEffect(() => {
    previewIframeRef.current?.contentWindow?.postMessage({ type: 'ob_preview_step', step: previewStep }, '*');
  }, [previewStep]);

  useEffect(() => {
    const onMsg = (e) => { if (e.data?.type === 'ob_preview_ready') pushPreview(form, previewStep); };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [form, previewStep, pushPreview]);

  const publicUrl = form ? `${window.location.origin}/onboard/${form.slug}` : '';
  const previewUrl = form ? `${window.location.origin}/onboard/${form.slug}/preview` : '';
  const embedCode = form
    ? `<iframe id="vox-onboard" src="${publicUrl}?embed=1" style="width:100%;min-height:900px;border:none;display:block;" title="Client Onboarding Form" scrolling="no"></iframe>
<script>window.addEventListener("message",function(e){if(e.data&&e.data.type==="ob_embed_height"&&e.data.height){var f=document.getElementById("vox-onboard");if(f){f.style.height=e.data.height+"px";f.style.minHeight="0";}}});</script>`
    : '';

  const copy = (txt, label) => {
    navigator.clipboard.writeText(txt);
    toast.success(`${label} copied`);
  };

  const sendTestWebhook = async () => {
    const url = (form.webhook_url || '').trim();
    if (!url) { toast.error('Paste your GHL inbound webhook URL first'); return; }
    setTestingWebhook(true);
    setWebhookResult(null);
    try {
      const res = await api.post(`/api/workspaces/${workspaceId}/onboarding-form/test-webhook`, { webhook_url: url });
      setWebhookResult(res.data);
      if (res.data.ok) toast.success('Test data sent — check your GHL workflow mapping');
      else toast.error(`Webhook responded with ${res.data.status_code || 'an error'}`);
    } catch (e) {
      setWebhookResult({ ok: false, error: e.response?.data?.detail || 'Request failed' });
      toast.error(e.response?.data?.detail || 'Test failed');
    } finally {
      setTestingWebhook(false);
    }
  };

  if (!form) return (
    <DashboardLayout>
      <div className="flex items-center justify-center py-32"><Loader2 className="animate-spin text-teal-400" size={30} /></div>
    </DashboardLayout>
  );

  const colors = form.outline_colors || ['#38bdf8', '#a855f7', '#f43f5e'];
  const gradient = form.outline_type === 'solid' ? colors[0] : `linear-gradient(135deg, ${colors.join(', ')})`;

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 space-y-6" data-testid="onboarding-builder-page">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-sky-400 to-violet-500 flex items-center justify-center">
              <ClipboardList className="text-white" size={22} />
            </div>
            <div>
              <h1 className={`text-2xl font-bold ${text}`}>Client Onboarding</h1>
              <p className={`text-sm ${sub}`}>One form → sub-account, branding & AI agent. Automatically.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a href={previewUrl} target="_blank" rel="noreferrer"
              className={`px-4 py-2.5 rounded-xl border ${border} ${sub} text-sm font-medium hover:text-teal-400 flex items-center gap-2`}
              data-testid="ob-preview-live-btn">
              <ExternalLink size={15} /> Preview live form
            </a>
            <button onClick={save} disabled={saving}
              className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-teal-400 to-teal-500 text-[#08090d] text-sm font-semibold hover:shadow-lg hover:shadow-teal-400/25 disabled:opacity-60 flex items-center gap-2"
              data-testid="ob-save-btn">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className={`flex gap-1 p-1 rounded-xl w-fit border ${border} ${cardBg}`}>
          {[{ id: 'builder', label: 'Builder' }, { id: 'submissions', label: `Submissions (${submissions.length})` }].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab === t.id ? 'bg-teal-400/15 text-teal-400' : sub}`}
              data-testid={`ob-tab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'builder' && (
          <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
            {/* Settings */}
            <div className="xl:col-span-3 space-y-5">
              {/* Appearance */}
              <div className={`p-5 rounded-2xl border ${border} ${cardBg}`}>
                <div className="flex items-center gap-2 mb-4">
                  <Palette size={16} className="text-teal-400" />
                  <h3 className={`font-semibold ${text}`}>Appearance</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Theme</label>
                    <div className="flex gap-2">
                      {[{ v: 'dark', icon: Moon, l: 'Dark' }, { v: 'light', icon: Sun, l: 'Light' }].map(o => (
                        <button key={o.v} onClick={() => set('theme', o.v)}
                          className={`flex-1 px-3 py-2.5 rounded-lg border text-sm flex items-center justify-center gap-2 ${form.theme === o.v ? 'border-teal-400/50 bg-teal-400/10 text-teal-400' : `${border} ${sub}`}`}
                          data-testid={`ob-theme-${o.v}`}>
                          <o.icon size={14} /> {o.l}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Outline style</label>
                    <div className="flex gap-2">
                      {['gradient', 'solid'].map(o => (
                        <button key={o} onClick={() => set('outline_type', o)}
                          className={`flex-1 px-3 py-2.5 rounded-lg border text-sm capitalize ${form.outline_type === o ? 'border-teal-400/50 bg-teal-400/10 text-teal-400' : `${border} ${sub}`}`}
                          data-testid={`ob-outline-${o}`}>
                          {o}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>{form.outline_type === 'solid' ? 'Outline color' : 'Gradient colors'}</label>
                    <div className="flex items-center gap-3">
                      {(form.outline_type === 'solid' ? [0] : [0, 1, 2]).map(i => (
                        <input key={i} type="color" value={colors[i] || '#38bdf8'} onChange={e => setColor(i, e.target.value)}
                          className="w-12 h-10 rounded-lg cursor-pointer border-none bg-transparent" data-testid={`ob-color-${i}`} />
                      ))}
                      <div className="flex-1 h-3 rounded-full" style={{ background: gradient }} />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Glow / shadow color</label>
                    <input type="color" value={form.glow_color || '#2dd4bf'} onChange={e => set('glow_color', e.target.value)}
                      className="w-12 h-10 rounded-lg cursor-pointer border-none bg-transparent" data-testid="ob-glow-color" />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className={`p-5 rounded-2xl border ${border} ${cardBg}`}>
                <div className="flex items-center gap-2 mb-4">
                  <Video size={16} className="text-teal-400" />
                  <h3 className={`font-semibold ${text}`}>Header & Video</h3>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className={labelCls}>Form title</label>
                    <input value={form.form_title || ''} onChange={e => set('form_title', e.target.value)} className={inputCls} data-testid="ob-input-title" />
                  </div>
                  <div>
                    <label className={labelCls}>Form subtitle</label>
                    <input value={form.form_subtitle || ''} onChange={e => set('form_subtitle', e.target.value)} className={inputCls} data-testid="ob-input-subtitle" />
                  </div>
                  <div>
                    <label className={labelCls}>Video walkthrough URL (Wistia, Loom or YouTube — optional)</label>
                    <input value={form.video_url || ''} onChange={e => set('video_url', e.target.value)} placeholder="https://www.loom.com/share/…" className={inputCls} data-testid="ob-input-video" />
                  </div>
                  <div>
                    <label className={labelCls}>Video CTA banner (shown above video)</label>
                    <input value={form.video_cta || ''} onChange={e => set('video_cta', e.target.value)} className={inputCls} data-testid="ob-input-video-cta" />
                  </div>
                </div>
              </div>

              {/* Questions config */}
              <div className={`p-5 rounded-2xl border ${border} ${cardBg}`}>
                <div className="flex items-center gap-2 mb-4">
                  <MessageSquare size={16} className="text-teal-400" />
                  <h3 className={`font-semibold ${text}`}>Question Settings</h3>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className={labelCls}>Communication platform (used in the comms question)</label>
                    <div className="flex flex-wrap gap-2">
                      {PLATFORMS.map(p => (
                        <button key={p} onClick={() => set('comm_platform', p)}
                          className={`px-3 py-1.5 rounded-lg border text-xs ${form.comm_platform === p ? 'border-teal-400/50 bg-teal-400/10 text-teal-400' : `${border} ${sub}`}`}
                          data-testid={`ob-platform-${p.toLowerCase().replace(/\s/g, '-')}`}>
                          {p}
                        </button>
                      ))}
                      <input value={PLATFORMS.includes(form.comm_platform) ? '' : (form.comm_platform || '')}
                        onChange={e => set('comm_platform', e.target.value)} placeholder="Custom…"
                        className={`px-3 py-1.5 rounded-lg border text-xs w-28 ${isDarkMode ? 'bg-white/5 border-white/10 text-white' : 'bg-gray-50 border-gray-300'}`} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Last step — Twilio & Region */}
              <div className={`p-5 rounded-2xl border ${border} ${cardBg}`} data-testid="ob-region-card">
                <div className="flex items-center gap-2 mb-4">
                  <Globe size={16} className="text-teal-400" />
                  <h3 className={`font-semibold ${text}`}>Last Step — Twilio & Region</h3>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className={labelCls}>Client region — controls address, business type & tax-ID wording on the form</label>
                    <div className="flex gap-2">
                      {[{ v: 'us', l: '🇺🇸 U.S. Based' }, { v: 'international', l: '🌍 International' }].map(o => (
                        <button key={o.v} onClick={() => set('region', o.v)}
                          className={`flex-1 px-3 py-2.5 rounded-lg border text-sm font-medium ${(form.region || 'us') === o.v ? 'border-teal-400/50 bg-teal-400/10 text-teal-400' : `${border} ${sub}`}`}
                          data-testid={`ob-region-${o.v}`}>
                          {o.l}
                        </button>
                      ))}
                    </div>
                    <p className={`text-[11px] mt-1.5 ${sub}`}>
                      {(form.region || 'us') === 'us'
                        ? 'Country auto-fills to United States. Business types & EIN use U.S. terminology.'
                        : 'Clients pick their country. Business types & Tax ID / VAT wording are internationalized.'}
                    </p>
                  </div>
                  <div>
                    <label className={labelCls}>Default phone country (pre-selected country code on the form)</label>
                    <select value={form.default_phone_country || 'US'} onChange={e => set('default_phone_country', e.target.value)} className={inputCls} data-testid="ob-default-phone-country">
                      {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.flag} {c.name} ({c.dial})</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}><Mail size={11} className="inline mr-1" />Twilio A2P invite email (your email — clients invite it as admin)</label>
                    <input value={form.twilio_invite_email || ''} onChange={e => set('twilio_invite_email', e.target.value)} placeholder="tech@youragency.com" className={inputCls} data-testid="ob-input-twilio-email" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls}>Twilio step video URL (Wistia, Loom or YouTube — optional)</label>
                      <input value={form.twilio_video_url || ''} onChange={e => set('twilio_video_url', e.target.value)} placeholder="https://www.loom.com/share/…" className={inputCls} data-testid="ob-input-twilio-video" />
                    </div>
                    <div>
                      <label className={labelCls}>Twilio video banner text</label>
                      <input value={form.twilio_video_cta || ''} onChange={e => set('twilio_video_cta', e.target.value)} className={inputCls} data-testid="ob-input-twilio-video-cta" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Thank you */}
              <div className={`p-5 rounded-2xl border ${border} ${cardBg}`}>
                <div className="flex items-center gap-2 mb-4">
                  <Mail size={16} className="text-teal-400" />
                  <h3 className={`font-semibold ${text}`}>Thank-You Page</h3>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className={labelCls}>Thank-you headline — use {'{{first_name}}'} to personalize</label>
                    <textarea rows={2} value={form.thankyou_headline || ''} onChange={e => set('thankyou_headline', e.target.value)} className={inputCls} data-testid="ob-input-ty-headline" />
                  </div>
                  <div>
                    <label className={labelCls}>Thank-you subtext (supports multiple lines)</label>
                    <textarea rows={3} value={form.thankyou_subtext || ''} onChange={e => set('thankyou_subtext', e.target.value)} className={inputCls} data-testid="ob-input-ty-subtext" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls}>CTA button text</label>
                      <input value={form.thankyou_cta_text || ''} onChange={e => set('thankyou_cta_text', e.target.value)} className={inputCls} data-testid="ob-input-ty-cta-text" />
                    </div>
                    <div>
                      <label className={labelCls}>CTA link (e.g. kickoff call booking)</label>
                      <input value={form.thankyou_cta_url || ''} onChange={e => set('thankyou_cta_url', e.target.value)} placeholder="https://calendly.com/…" className={inputCls} data-testid="ob-input-ty-cta-url" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right column: Embed + Preview */}
            <div className="xl:col-span-2 space-y-5">
              {/* Embed & Share */}
              <div className={`p-5 rounded-2xl border ${justSaved ? 'border-teal-400/40' : border} ${cardBg}`} data-testid="ob-embed-panel">
                <div className="flex items-center gap-2 mb-1">
                  <Check size={16} className="text-teal-400" />
                  <h3 className={`font-semibold ${text}`}>Embed & Share</h3>
                </div>
                <p className={`text-xs mb-4 ${sub}`}>Add this form to any site — GHL funnels, WordPress, Webflow…</p>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => copy(publicUrl, 'Link')}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-teal-400/15 text-teal-400 text-sm font-medium hover:bg-teal-400/25 transition-colors"
                    data-testid="ob-copy-link-btn"
                  >
                    <Link2 size={14} /> Copy Hosted Link
                  </button>
                  <button
                    onClick={() => setShowEmbedModal(true)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border ${border} ${sub} text-sm font-medium hover:text-teal-400 hover:border-teal-400/40 transition-colors`}
                    data-testid="ob-embed-code-btn"
                  >
                    <Code2 size={14} /> Get Embed Code
                  </button>
                </div>
              </div>

              {/* Interactive live preview — real form in an iframe, synced while editing */}
              <div className={`p-5 rounded-2xl border ${border} ${cardBg}`} data-testid="ob-live-preview-card">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <h3 className={`font-semibold ${text}`}>Live Preview</h3>
                  <div className={`flex gap-1 p-1 rounded-lg border ${border}`}>
                    {['Start', 'Details', 'Last step', 'Thank you'].map((label, i) => (
                      <button
                        key={label}
                        onClick={() => setPreviewStep(i)}
                        className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-all ${previewStep === i ? 'bg-teal-400/15 text-teal-400' : sub}`}
                        data-testid={`ob-preview-step-${i}`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className={`rounded-xl overflow-hidden border ${border}`}>
                  <iframe
                    ref={previewIframeRef}
                    src={`/onboard/${form.slug}?embed=1&preview=1`}
                    title="Form live preview"
                    className="w-full"
                    style={{ height: '620px', border: 'none' }}
                    onLoad={() => pushPreview(form, previewStep)}
                    data-testid="ob-preview-iframe"
                  />
                </div>
                <p className={`text-[11px] mt-2 ${sub}`}>Updates live as you edit — switch pages above to check every step.</p>
              </div>

              {/* GHL Webhook */}
              <div className={`p-5 rounded-2xl border ${border} ${cardBg}`} data-testid="ob-webhook-card">
                <div className="flex items-center gap-2 mb-1">
                  <Webhook size={16} className="text-teal-400" />
                  <h3 className={`font-semibold ${text}`}>GHL Inbound Webhook</h3>
                </div>
                  <label className={labelCls}>Webhook URL — full submission POSTed here</label>
                  <input value={form.webhook_url || ''} onChange={e => { set('webhook_url', e.target.value); setWebhookResult(null); }} placeholder="https://services.leadconnectorhq.com/hooks/…" className={inputCls} data-testid="ob-input-webhook" />
                  <p className={`text-[11px] mt-1.5 ${sub}`}>In GHL: Workflows → Trigger "Inbound Webhook" → copy the URL and paste it here. Use the fields below to build your contact mapping, SMS &amp; email automations.</p>

                  {/* Send test data */}
                  <div className="flex flex-wrap items-center gap-2.5 mt-3">
                    <button
                      onClick={sendTestWebhook}
                      disabled={testingWebhook || !(form.webhook_url || '').trim()}
                      className="px-4 py-2 rounded-lg bg-sky-400/15 text-sky-400 border border-sky-400/30 text-xs font-semibold hover:bg-sky-400/25 transition-all disabled:opacity-50 flex items-center gap-1.5"
                      data-testid="ob-send-test-webhook"
                    >
                      {testingWebhook ? <Loader2 size={13} className="animate-spin" /> : <Zap size={13} />}
                      {testingWebhook ? 'Sending…' : 'Send Test Data'}
                    </button>
                    {webhookResult && (
                      webhookResult.ok ? (
                        <span className="flex items-center gap-1.5 text-xs font-medium text-teal-400 bg-teal-400/10 px-3 py-1.5 rounded-lg" data-testid="ob-webhook-test-success">
                          <Check size={13} /> Test data received ({webhookResult.status_code}) — map your fields in GHL
                        </span>
                      ) : (
                        <span className="flex items-center gap-1.5 text-xs font-medium text-red-400 bg-red-400/10 px-3 py-1.5 rounded-lg" data-testid="ob-webhook-test-fail">
                          <X size={13} /> {webhookResult.error || `Failed (${webhookResult.status_code})`}
                        </span>
                      )
                    )}
                  </div>

                  {/* Field reference dropdown */}
                  <button
                    onClick={() => setShowWebhookFields(v => !v)}
                    className={`mt-3 flex items-center gap-1.5 text-xs font-medium ${sub} hover:text-teal-400 transition-colors`}
                    data-testid="ob-toggle-webhook-fields"
                  >
                    <ChevronDown size={14} className={`transition-transform ${showWebhookFields ? 'rotate-180' : ''}`} />
                    {showWebhookFields ? 'Hide' : 'View'} the {WEBHOOK_FIELD_GROUPS.reduce((n, g) => n + g.fields.length, 0)} data fields that will be sent
                  </button>
                  {showWebhookFields && (
                    <div className={`mt-3 rounded-xl border ${border} overflow-hidden`} data-testid="ob-webhook-fields-panel">
                      {WEBHOOK_FIELD_GROUPS.map(g => (
                        <div key={g.group}>
                          <p className={`px-4 py-2 text-[10px] font-bold uppercase tracking-wider ${sub} ${isDarkMode ? 'bg-white/[0.04]' : 'bg-gray-100'}`}>{g.group}</p>
                          {g.fields.map(([key, example]) => (
                            <div key={key} className={`flex items-start gap-3 px-4 py-2 border-t ${border}`}>
                              <code className="text-[11px] text-teal-400 font-mono shrink-0 w-44 truncate">{key}</code>
                              <span className={`text-[11px] ${sub} truncate`}>{example}</span>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
              </div>

            </div>
          </div>
        )}

        {tab === 'submissions' && (
          <div className={`rounded-2xl border ${border} ${cardBg} overflow-hidden`} data-testid="ob-submissions-panel">
            {submissions.length === 0 ? (
              <div className="text-center py-16">
                <Inbox size={36} className={`mx-auto mb-3 opacity-40 ${sub}`} />
                <p className={sub}>No submissions yet — share your form link to onboard your first client.</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className={`text-left text-xs uppercase tracking-wider ${sub} border-b ${border}`}>
                    <th className="px-5 py-3">Business</th>
                    <th className="px-5 py-3 hidden sm:table-cell">Contact</th>
                    <th className="px-5 py-3 hidden md:table-cell">Email</th>
                    <th className="px-5 py-3">Status</th>
                    <th className="px-5 py-3 hidden sm:table-cell">Date</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {submissions.map(s => (
                    <tr key={s.submission_id} className={`border-b ${border} last:border-0`} data-testid={`ob-submission-row-${s.submission_id}`}>
                      <td className={`px-5 py-3.5 font-medium ${text}`}>{s.business_name}</td>
                      <td className={`px-5 py-3.5 hidden sm:table-cell ${sub}`}>{s.contact_name}</td>
                      <td className={`px-5 py-3.5 hidden md:table-cell ${sub}`}>{s.contact_email}</td>
                      <td className="px-5 py-3.5">
                        <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${
                          s.status === 'completed' ? 'bg-teal-400/10 text-teal-400' : 'bg-amber-400/10 text-amber-400'
                        }`}>
                          {s.status === 'completed' ? 'SUB-ACCOUNT CREATED' : s.status.replace(/_/g, ' ').toUpperCase()}
                        </span>
                      </td>
                      <td className={`px-5 py-3.5 hidden sm:table-cell text-xs ${sub}`}>{(s.created_at || '').slice(0, 10)}</td>
                      <td className="px-5 py-3.5 text-right">
                        {s.sub_account_id && (
                          <button
                            onClick={() => navigate(`/workspace/${workspaceId}/sub-account/${s.sub_account_id}/brand-profiles`)}
                            className="text-teal-400 text-xs font-medium inline-flex items-center gap-1 hover:underline"
                            data-testid={`ob-open-branding-${s.submission_id}`}>
                            Open Branding <ArrowRight size={12} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Embed code modal */}
        {showEmbedModal && form && (
          <div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[70] p-4"
            onClick={() => setShowEmbedModal(false)}
            data-testid="ob-embed-modal"
          >
            <div
              className={`w-full max-w-lg rounded-2xl border ${border} ${cardBg} p-6`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <Code2 size={16} className="text-teal-400" />
                  <h3 className={`font-semibold ${text}`}>Embed Code</h3>
                </div>
                <button onClick={() => setShowEmbedModal(false)} className={`p-1.5 rounded-lg ${sub} hover:text-teal-400`} data-testid="ob-embed-modal-close">
                  <X size={16} />
                </button>
              </div>
              <p className={`text-xs mb-4 ${sub}`}>Paste this snippet into any page — GHL funnels, WordPress, Webflow…</p>
              <textarea readOnly rows={5} value={embedCode} className={`${inputCls} text-[11px] font-mono`} data-testid="ob-embed-code" />
              <button
                onClick={() => { copy(embedCode, 'Embed code'); setShowEmbedModal(false); }}
                className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-teal-400 to-teal-500 text-[#08090d] text-sm font-semibold hover:opacity-90 transition-opacity"
                data-testid="ob-copy-embed-btn"
              >
                <Copy size={14} /> Copy Embed Code
              </button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
